
import { computed, isRef } from '@nuxtjs/composition-api';
import useProductType from '~/composable/useProductType';
import useTlmCart from '~/composable/useTlmCart';
import { CART_ITEM_GROUP_PURCHASE_TYPE } from '~/constants/CheckoutFlow';


// eslint-disable-next-line complexity
export default function useCartQueries (appContext, cart) {
  const { isSubscription, isMobileBroadBandSubscription, isMobileVoiceSubscription } = useProductType()
  const hubbleClient = appContext.$hubble;
  const { cart: loadedCart, loadOrCreateCart, containsKeepSubscription } = useTlmCart(appContext);
  const loadCart = async () => {
    await loadOrCreateCart();
    return loadedCart;
  }

  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context to use this composable');
  }
  if (!isRef(cart) || !cart.value || Object.keys(cart.value).length < 1) {
    cart = loadCart();
  }
  const isSignUpPurchaseType = (cartItemGroupPurchaseType) => {
    return cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_NEW_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_NP_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_NP_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_NEW_SUBSCRIPTION
  }
  const isChangeSubscriptionPurchaseType = (cartItemGroupPurchaseType) => {
    return cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_CHANGE_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_CHANGE_SUBSCRIPTION
  }
  const isKeepSubscriptionPurchaseType = (cartItemGroupPurchaseType) => {
    return cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_KEEPING_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_KEEPING_SUBSCRIPTION
  }
  const getDeviceWithInstallment = computed(() => {
    const devices = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey) &&
        cartItem.installmentPeriod > 0)
      if (device !== undefined) {
        return devices.push(device)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return devices.length > 0 ? devices[0] : []
  })
  const playMixProducts = computed(() => {
    const playMixItems = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const playMixSubscription = cartItemGroup.cartItems.find(cartItem => isSubscription(cartItem.cartProduct.productTypeKey) && cartItem.cartProduct.additionalBillingInformation.isMixProduct)
      if (playMixSubscription !== undefined) {
        return playMixItems.push(playMixSubscription)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return playMixItems[0]
  })
  const cartOnlyContainsSubscription = computed(() => { // (sim-only)
    let subscriptionOnly = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const notSubscription = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (!notSubscription) {
        subscriptionOnly = true
      }
    })
    return subscriptionOnly
  })
  const isAnyItemAssociatedWithMobileVoiceSubscription = computed(() => {
    let isAnyItemAssociatedWithMobileVoiceSubscription = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThSubscription = cartItemGroup.cartItems.find(cartItem => isMobileVoiceSubscription(cartItem.cartProduct.productTypeKey))
      if (carteItemWiThSubscription !== undefined) {
        isAnyItemAssociatedWithMobileVoiceSubscription = true;
      }
    })
    return isAnyItemAssociatedWithMobileVoiceSubscription;
  })
  const isAnyItemAssociatedWithMobileBroadBand = computed(() => {
    let isAnyItemAssociatedWithMobileBroadBand = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThSubscription = cartItemGroup.cartItems.find(cartItem => isMobileBroadBandSubscription(cartItem.cartProduct.productTypeKey))
      if (carteItemWiThSubscription !== undefined) {
        isAnyItemAssociatedWithMobileBroadBand = true;
      }
    })
    return isAnyItemAssociatedWithMobileBroadBand;
  })
  const isAnyItemAssociatedWithSubscription = computed(() => {
    let isAnyItemAssociatedWithSubscription = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThSubscription = cartItemGroup.cartItems.find(cartItem => isMobileVoiceSubscription(cartItem.cartProduct.productTypeKey) || isMobileBroadBandSubscription(cartItem.cartProduct.productTypeKey))
      if (carteItemWiThSubscription !== undefined) {
        isAnyItemAssociatedWithSubscription = true;
      }
    })
    return isAnyItemAssociatedWithSubscription;
  })
  const hasValidDataForPurchaseScenario = computed(() => {
    let hasValidData = true
    // eslint-disable-next-line complexity
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const cartItemGroupPurchaseType = cartItemGroup.cartItemGroupPurchaseType
      const subscriptionItem = cartItemGroup.cartItems.find(item => isSubscription(item.cartProduct.productTypeKey))
      if (isSignUpPurchaseType(cartItemGroupPurchaseType)) {
        const phoneNumberAttr = subscriptionItem.itemAttributes.find(attr => attr.name === 'PhoneNumber')
        hasValidData = phoneNumberAttr !== undefined && phoneNumberAttr !== '' && phoneNumberAttr !== null
      } else if (containsKeepSubscription.value === true) {
        // do some validation
        return hasValidData;
      } else if (isChangeSubscriptionPurchaseType(cartItemGroupPurchaseType)) {
        const prepaidAccountIdAttr = subscriptionItem.itemAttributes.find(attr => attr.name === 'PrepaidAccountId')
        hasValidData = prepaidAccountIdAttr !== undefined && prepaidAccountIdAttr !== '' && prepaidAccountIdAttr !== null
      } else if (cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.SUBSCRIPTION_ONLY) {
        const phoneNumberAttr = subscriptionItem.itemAttributes.find(attr => attr.name === 'PhoneNumber')
        const prepaidAccountIdAttr = subscriptionItem.itemAttributes.find(attr => attr.name === 'PrepaidAccountId')
        hasValidData = (phoneNumberAttr !== undefined && phoneNumberAttr !== '' && phoneNumberAttr !== null) ||
          (prepaidAccountIdAttr !== undefined && prepaidAccountIdAttr !== '' && prepaidAccountIdAttr !== null)
      }
    })
    return hasValidData
  })
  const cartItemGroupPurchaseType = computed(() => {
    const cartItemGroupPurchaseType = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      cartItemGroupPurchaseType.push(cartItemGroup.cartItemGroupPurchaseType)
    })
    return cartItemGroupPurchaseType
  })
  return {
    cartItemGroupPurchaseType,
    isKeepSubscriptionPurchaseType,
    hasValidDataForPurchaseScenario,
    getDeviceWithInstallment,
    playMixProducts,
    cartOnlyContainsSubscription,
    isAnyItemAssociatedWithMobileVoiceSubscription,
    isAnyItemAssociatedWithMobileBroadBand,
    isAnyItemAssociatedWithSubscription
  }
}
